/**
 * Générateur — Automatisme : le périmètre du carré et du rectangle
 * Attendu ID : 1024 | Niveau : 6ème | Domaine : GM
 * Rubrique : AUTOMATISME (programme 2025) — « Il sait que le périmètre d'une
 * figure plane est la longueur de son contour. L'élève sait calculer le
 * périmètre d'un carré et d'un rectangle. »
 * Labels (cartes exerciseur) :
 *   - Périmètre du carré et du rectangle
 *
 * ── Plan de cartes (2026-08-21, validé prof) ───────────────────────────────
 * DEUX volets couverts (pas un gen « deux formules ») :
 *   - FIGURE (saisie) : un polygone coté SANS énoncé verbal — triangle,
 *     rectangle/carré codés, figure en L composée (~1/5, TOUTES les cotes
 *     données : le but est de comprendre que le périmètre est le CONTOUR) ;
 *   - CALCUL (saisie) : carré / rectangle verbal, entiers et demis, cm
 *     majoritaire + m en appoint ;
 *   - QCM : sens (« le contour » vs « la surface », l'erreur cible) et
 *     calcul-à-choisir — la bonne réponse est « 5 + 3 + 5 + 3 », LA
 *     procédure du cours moyen (la formule 2 × (L + l) n'y est PAS au
 *     programme : elle n'apparaît dans AUCUN choix, elle serait une seconde
 *     bonne réponse) ;
 *   - V/F : le sens du mot, la vraisemblance (parité du périmètre d'un
 *     rectangle à côtés entiers) et des calculs à erreurs nommées ;
 *   - INVERSE (texte à trous échafaudé, décision prof) : carré P → côté,
 *     rectangle P + longueur → demi-périmètre puis largeur.
 * ARITHMÉTIQUE en millimètres entiers (les « ,5 » exacts, zéro flottant) ;
 * division toujours en mots, jamais le symbole ÷ ; figures SVG construites
 * depuis les côtés (jamais l'inverse), inclusion vérifiée.
 */
import {
    randomChoice, randInt, shuffle,
    makeQcm, makeVraiFaux, makeTexteTrou, makeSaisie,
} from '../../../utils';

const ATTENDU_ID = 1024;
const opts = (label) => ({ niveau: '6eme', matiere: 'maths', attendu: ATTENDU_ID, label });

const L1 = 'Périmètre du carré et du rectangle';
const NOIR = '#111827';

const garde = (condition, message) => {
    if (!condition) throw new Error(`gen_1024 : ${message}`);
};

/** mm entiers → écriture française en cm (85 → « 8,5 »). */
const mmVersCm = (mm) => {
    garde(Number.isInteger(mm) && mm > 0, 'mm non entier');
    const ent = Math.floor(mm / 10);
    const dec = mm % 10;
    return dec === 0 ? String(ent) : `${ent},${dec}`;
};

// ─── Figures SVG (construites depuis les côtés, jamais l'inverse) ─────────

const dataUrl = (svg) => `data:image/svg+xml,${encodeURIComponent(svg)}`;
const elImage = (url) => ({ version: 1, elements: [{ type: 'image', config: { imageUrl: url } }] });

const BLEU_FIG = '#1E3A8A';
const FOND_FIG = '#eff6ff';

/** Emballe des points (px) dans un viewBox 340 × 280 avec marges (throw si
 *  impossible) et retourne le décalage à appliquer. */
const cadrer = (xs, ys, marge = 46) => {
    const minX = Math.min(...xs); const maxX = Math.max(...xs);
    const minY = Math.min(...ys); const maxY = Math.max(...ys);
    garde(maxX - minX <= 340 - 2 * marge && maxY - minY <= 280 - 2 * marge, 'figure trop grande');
    return {
        dx: marge - minX + (340 - 2 * marge - (maxX - minX)) / 2,
        dy: marge - minY + (280 - 2 * marge - (maxY - minY)) / 2,
    };
};

/** Orientation (signe de l'aire) d'un polygone en coordonnées écran. */
const orientation = (pts) => {
    let aire = 0;
    for (let k = 0; k < pts.length; k++) {
        const [x1, y1] = pts[k];
        const [x2, y2] = pts[(k + 1) % pts.length];
        aire += x1 * y2 - x2 * y1;
    }
    return Math.sign(aire) || 1;
};

/** FLÈCHE DE COTE (exigence prof 2026-08-21) : une double flèche décalée à
 *  l'EXTÉRIEUR du côté, d'une extrémité à l'autre, étiquette au-delà —
 *  les cotes (noires) ne se confondent pas avec la figure (bleue). */
const cote = (p1, p2, txt, sens, face) => {
    const dx = p2[0] - p1[0]; const dy = p2[1] - p1[1];
    const lon = Math.hypot(dx, dy) || 1;
    const ux = dx / lon; const uy = dy / lon;
    // normale extérieure (orientation du polygone), retournée si face = −1 :
    // deux cotes qui SE SUIVENT alternent dehors / dedans (prof 2026-08-21)
    const nx = face * sens * dy / lon; const ny = -face * sens * dx / lon;
    const OFF = 15;                                  // décalage de la flèche
    const a = [p1[0] + nx * OFF, p1[1] + ny * OFF];
    const b = [p2[0] + nx * OFF, p2[1] + ny * OFF];
    const tete = (pt, dirx, diry) =>
        `<path d="M ${pt[0].toFixed(1)} ${pt[1].toFixed(1)} l ${(dirx * 8 + nx * 3).toFixed(1)} ${(diry * 8 + ny * 3).toFixed(1)} m ${(-dirx * 8 - nx * 3).toFixed(1)} ${(-diry * 8 - ny * 3).toFixed(1)} l ${(dirx * 8 - nx * 3).toFixed(1)} ${(diry * 8 - ny * 3).toFixed(1)}" stroke="${NOIR}" stroke-width="1.1" fill="none"/>`;
    const mx = (a[0] + b[0]) / 2 + nx * 15;
    const my = (a[1] + b[1]) / 2 + ny * 15;
    return `<line x1="${a[0].toFixed(1)}" y1="${a[1].toFixed(1)}" x2="${b[0].toFixed(1)}" y2="${b[1].toFixed(1)}" stroke="${NOIR}" stroke-width="1.1" stroke-dasharray="4 3"/>`
        + tete(a, ux, uy) + tete(b, -ux, -uy)
        + `<text x="${mx.toFixed(1)}" y="${(my + 4.5).toFixed(1)}" font-size="13" font-weight="700" fill="${NOIR}" text-anchor="middle" font-family="system-ui,sans-serif">${txt}</text>`;
};

/** Polygone bleu + une flèche de cote par côté listé. `faces` (optionnel) :
 *  +1 dehors / −1 dedans, côté par côté — un PLAN DE PLACEMENT fixe vaut
 *  mieux qu'une alternance aveugle (deux cotes intérieures se télescopaient,
 *  vécu prof 2026-08-21). Triangle, carré, rectangle : tout dehors. */
const dessinerPolygone = (pts, cotesTxt, aria, faces = null) => {
    const sens = orientation(pts);
    let svg = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 340 280" width="340" role="img" aria-label="${aria}">`
        + `<polygon points="${pts.map((p) => `${p[0].toFixed(1)},${p[1].toFixed(1)}`).join(' ')}" fill="${FOND_FIG}" stroke="${BLEU_FIG}" stroke-width="2.6"/>`;
    for (let k = 0; k < pts.length; k++) {
        const txt = cotesTxt[k];
        if (!txt) continue;
        svg += cote(pts[k], pts[(k + 1) % pts.length], txt, sens, faces ? faces[k] : 1);
    }
    return svg + '</svg>';
};

/** Triangle de côtés (a, b, c) en cm entiers. */
const TRIANGLES = [[3, 4, 5], [4, 5, 6], [5, 6, 7], [4, 6, 7], [5, 5, 6], [6, 7, 8], [5, 7, 8]];
const figureTriangle = () => {
    const [a, b, c] = shuffle([...randomChoice(TRIANGLES)]);
    const S = 24;
    const xA = (b * b + c * c - a * a) / (2 * c);
    const ySq = b * b - xA * xA;
    garde(ySq > 0.2, 'triangle dégénéré');
    const yA = Math.sqrt(ySq);
    const pts0 = [[0, 0], [c, 0], [xA, -yA]].map(([x, y]) => [x * S, y * S]);
    const { dx, dy } = cadrer(pts0.map((p) => p[0]), pts0.map((p) => p[1]));
    const P = pts0.map(([x, y]) => [x + dx, y + dy]);
    const svg = dessinerPolygone(P, [`${c} cm`, `${a} cm`, `${b} cm`],
        `Un triangle dont les côtés mesurent ${a} cm, ${b} cm et ${c} cm`);
    return { image: elImage(dataUrl(svg)), perimetreMm: (a + b + c) * 10 };
};

/** Rectangle (ou carré) coté sur la figure, sans énoncé verbal. */
const figureRectangle = (carre) => {
    const Lmm = carre ? randInt(3, 8) * 10 : randInt(5, 10) * 10;
    const lmm = carre ? Lmm : randInt(2, Math.min(7, Lmm / 10 - 1)) * 10;
    const S = 2.2;
    const w = Lmm * S; const h = lmm * S;
    const { dx, dy } = cadrer([0, w], [0, h]);
    const P = [[dx, dy], [dx + w, dy], [dx + w, dy + h], [dx, dy + h]];
    const cotes = carre
        ? [`${mmVersCm(Lmm)} cm`, null, null, null]
        : [`${mmVersCm(Lmm)} cm`, `${mmVersCm(lmm)} cm`, null, null];
    let svg = dessinerPolygone(P, cotes,
        carre ? `Un carré de côté ${mmVersCm(Lmm)} cm` : `Un rectangle de ${mmVersCm(Lmm)} cm sur ${mmVersCm(lmm)} cm`);
    if (carre) {
        // codage du carré : un petit trait d'égalité au milieu de chaque côté
        const marques = [[dx + w / 2, dy + h], [dx, dy + h / 2], [dx + w, dy + h / 2]];
        const traits = marques.map(([x, y]) =>
            `<line x1="${(x - 5).toFixed(1)}" y1="${(y - 5).toFixed(1)}" x2="${(x + 5).toFixed(1)}" y2="${(y + 5).toFixed(1)}" stroke="${BLEU_FIG}" stroke-width="2"/>`).join('');
        svg = svg.replace('</svg>', `${traits}</svg>`);
    }
    return { image: elImage(dataUrl(svg)), perimetreMm: 2 * (Lmm + lmm) };
};

/** Figure en L (deux rectangles accolés), TOUTES les cotes données. */
const figureComposee = () => {
    const W = randInt(6, 9); const H = randInt(5, 8);
    const w = randInt(2, W - 2); const h = randInt(2, H - 2);
    const S = 19;
    const y0 = H * S;
    const pts0 = [[0, y0], [W * S, y0], [W * S, h * S], [(W - w) * S, h * S], [(W - w) * S, 0], [0, 0]];
    const { dx, dy } = cadrer(pts0.map((p) => p[0]), pts0.map((p) => p[1]));
    const P = pts0.map(([x, y]) => [x + dx, y + dy]);
    const cotesTxt = [];
    let somme = 0;
    for (let k = 0; k < P.length; k++) {
        const p1 = P[k]; const p2 = P[(k + 1) % P.length];
        const lonCm = Math.round(Math.hypot(p2[0] - p1[0], p2[1] - p1[1]) / S);
        garde(lonCm >= 2, 'côté du L trop court');
        somme += lonCm;
        cotesTxt.push(`${lonCm} cm`);
    }
    garde(somme === 2 * (W + H), 'périmètre du L incohérent');
    const svg = dessinerPolygone(P, cotesTxt,
        `Une figure en L dont les côtés mesurent ${cotesTxt.map((t) => t).join(', ')}`,
        // bas, droite, cran horizontal : dehors ; cran VERTICAL : dedans
        // (la colonne haute est libre) ; haut et gauche : dehors
        [1, 1, 1, -1, 1, 1]);
    return { image: elImage(dataUrl(svg)), perimetreMm: somme * 10 };
};

const genFigureSaisie = () => {
    const tirage = randInt(1, 10);
    let figure;
    let sousType;
    if (tirage <= 2) { figure = figureComposee(); sousType = 'compose'; }
    else if (tirage <= 5) { figure = figureTriangle(); sousType = 'triangle'; }
    else if (tirage <= 8) { figure = figureRectangle(false); sousType = 'rectangle'; }
    else { figure = figureRectangle(true); sousType = 'carre'; }
    const payload = makeSaisie(
        mmVersCm(figure.perimetreMm),
        '<p><b>Quel est le périmètre de cette figure ?</b></p>{{element:0}}',
        { ...opts(L1), suffixe: 'cm', elements_additionnels: figure.image },
    );
    payload.contenu = { ...payload.contenu, sousType };
    payload._meta.params = { sousType, perimetreMm: figure.perimetreMm };
    return payload;
};

// ─── Format 2 : le calcul direct (saisie verbale) ─────────────────────────

/** Tire un carré ou un rectangle en mm entiers (entiers ou demis en cm). */
const tirerPiece = ({ demis = true } = {}) => {
    const carre = Math.random() < 0.45;
    const demi = demis && Math.random() < 0.3;
    if (carre) {
        const cote = demi ? randInt(4, 24) * 5 : randInt(2, 12) * 10;
        return { carre, Lmm: cote, lmm: cote, perimetreMm: 4 * cote };
    }
    const Lmm = demi ? randInt(8, 24) * 5 : randInt(4, 12) * 10;
    const lmm = demi ? randInt(4, Lmm / 5 - 1) * 5 : randInt(2, Lmm / 10 - 1) * 10;
    garde(lmm < Lmm, 'largeur ≥ longueur');
    return { carre, Lmm, lmm, perimetreMm: 2 * (Lmm + lmm) };
};

const genCalculSaisie = () => {
    const piece = tirerPiece();
    const unite = Math.random() < 0.2 ? 'm' : 'cm';
    const texte = piece.carre
        ? `<p><b>Quel est le périmètre d'un carré de côté ${mmVersCm(piece.Lmm)} ${unite} ?</b></p>`
        : `<p><b>Quel est le périmètre d'un rectangle de ${mmVersCm(piece.Lmm)} ${unite} sur ${mmVersCm(piece.lmm)} ${unite} ?</b></p>`;
    return makeSaisie(mmVersCm(piece.perimetreMm), texte, { ...opts(L1), suffixe: unite });
};

// ─── Format 3 : QCM (le sens, et le calcul à choisir) ─────────────────────

// Le SENS en 3 formulations du même savoir (rééquilibrage prof 2026-08-21 :
// une question unique à 50 % du format se répétait à chaque session)
const SENS_VARIANTES = [
    {
        question: 'Le périmètre d\'une figure plane, qu\'est-ce que c\'est ?',
        bonne: 'La longueur de son contour',
        dis: [
            { valeur: 'La surface qu\'elle occupe', code: 'confusion_aire' },
            { valeur: 'La longueur de son plus grand côté', code: 'un_seul_cote' },
            { valeur: 'Le nombre de ses côtés', code: 'compte_de_cotes' },
        ],
    },
    {
        question: 'Que mesure-t-on quand on calcule le périmètre d\'une figure ?',
        bonne: 'La longueur de son contour',
        dis: [
            { valeur: 'La place qu\'elle occupe', code: 'confusion_aire' },
            { valeur: 'La longueur de son plus long côté', code: 'un_seul_cote' },
            { valeur: 'Combien elle a de côtés', code: 'compte_de_cotes' },
        ],
    },
    {
        question: 'Pour trouver le périmètre d\'une figure, que faut-il faire ?',
        bonne: 'Additionner les longueurs de tous ses côtés',
        dis: [
            { valeur: 'Multiplier sa longueur par sa largeur', code: 'confusion_aire' },
            { valeur: 'Mesurer son plus grand côté', code: 'un_seul_cote' },
            { valeur: 'Compter ses côtés', code: 'compte_de_cotes' },
        ],
    },
];

const genQcm = () => {
    if (Math.random() < 1 / 3) {
        const v = randomChoice(SENS_VARIANTES);
        const payload = makeQcm(
            [v.bonne, ...v.dis.map((c) => c.valeur)],
            v.bonne,
            `<p><b>${v.question}</b></p>`,
            opts(L1),
            false,
        );
        payload.contenu = { ...payload.contenu, sousType: 'sens' };
        payload._meta.params = { sousType: 'sens', distracteurs: v.dis };
        return payload;
    }
    // le CALCUL à choisir : la bonne réponse est la SOMME DES QUATRE CÔTÉS
    // (procédure du cours moyen — la formule 2 × (L + l), hors programme du
    // CM, n'apparaît dans AUCUN choix : elle serait une seconde bonne réponse)
    for (let essai = 0; essai < 30; essai++) {
        const Lcm = randInt(4, 12);
        const lcm = randInt(2, Lcm - 1);
        const bonne = `${Lcm} + ${lcm} + ${Lcm} + ${lcm}`;
        const dis = [
            { valeur: `${Lcm} × ${lcm}`, code: 'confusion_aire' },
            { valeur: `${Lcm} + ${lcm}`, code: 'demi_tour' },
            { valeur: `${Lcm} + ${Lcm} + ${lcm}`, code: 'cote_oublie' },
        ];
        // garde : aucune expression fausse ne doit VALOIR le périmètre
        const P = 2 * (Lcm + lcm);
        if (Lcm * lcm === P || Lcm + lcm === P || 2 * Lcm + lcm === P) continue;
        const payload = makeQcm(
            [bonne, ...dis.map((c) => c.valeur)],
            bonne,
            `<p><b>Quel calcul donne le périmètre d'un rectangle de ${Lcm} cm sur ${lcm} cm ?</b></p>`,
            opts(L1),
            false,
        );
        payload.contenu = { ...payload.contenu, sousType: 'calcul' };
        payload._meta.params = { sousType: 'calcul', Lcm, lcm, distracteurs: dis };
        return payload;
    }
    const payload = makeQcm(['5 + 3 + 5 + 3', '5 × 3', '5 + 3', '5 + 5 + 3'], '5 + 3 + 5 + 3',
        '<p><b>Quel calcul donne le périmètre d\'un rectangle de 5 cm sur 3 cm ?</b></p>', opts(L1), false);
    payload.contenu = { ...payload.contenu, sousType: 'calcul' };
    payload._meta.params = { sousType: 'calcul', Lcm: 5, lcm: 3, distracteurs: [] };
    return payload;
};

// ─── Format 4 : Vrai/Faux (sens, vraisemblance, calculs) ──────────────────

const LIGNES_SENS = [
    { t: 'Le périmètre d\'une figure est la longueur de son contour.', v: true },
    { t: 'Deux figures de formes différentes peuvent avoir le même périmètre.', v: true },
    { t: 'Le périmètre d\'une figure mesure la surface qu\'elle occupe.', v: false },
    { t: 'Le périmètre d\'un carré est quatre fois la longueur de son côté.', v: true },
];

const genVraiFaux = () => {
    for (let essai = 0; essai < 40; essai++) {
        const lignes = [];
        const verites = [];
        // 1 ou 2 lignes de sens
        for (const s of shuffle([...LIGNES_SENS]).slice(0, randInt(1, 2))) {
            lignes.push(s.t);
            verites.push(s.v);
        }
        // le reste : calculs et vraisemblance, vérité RE-CALCULÉE en mm
        const vus = new Set();
        for (let t = 0; t < 30 && lignes.length < 4; t++) {
            const piece = tirerPiece({ demis: false });
            const cle = `${piece.Lmm}|${piece.lmm}`;
            if (vus.has(cle)) continue;
            vus.add(cle);
            let annonceMm;
            if (Math.random() < 0.5) annonceMm = piece.perimetreMm;
            else {
                // faux NOMMÉS : aire, demi-tour, côté oublié, parité (± 1 cm)
                const faux = [
                    (piece.Lmm * piece.lmm) / 10,             // « aire » en valeur
                    piece.Lmm + piece.lmm,
                    2 * piece.Lmm + piece.lmm,
                    piece.perimetreMm + (Math.random() < 0.5 ? 10 : -10),
                ].filter((v) => Number.isInteger(v) && v > 0 && v !== piece.perimetreMm);
                annonceMm = randomChoice(faux);
            }
            const verite = annonceMm === piece.perimetreMm;
            const enonce = piece.carre
                ? `Un carré de côté ${mmVersCm(piece.Lmm)} cm a un périmètre de ${mmVersCm(annonceMm)} cm.`
                : `Un rectangle de ${mmVersCm(piece.Lmm)} cm sur ${mmVersCm(piece.lmm)} cm a un périmètre de ${mmVersCm(annonceMm)} cm.`;
            lignes.push(enonce);
            verites.push(verite);
        }
        const nbVraies = verites.filter(Boolean).length;
        if (lignes.length !== 4 || nbVraies < 1 || nbVraies > 3) continue;
        return makeVraiFaux(
            lignes,
            verites.map((v) => (v ? 0 : 1)),
            '<p><b>Vrai ou faux ?</b></p>',
            opts(L1),
        );
    }
    return makeVraiFaux(
        [LIGNES_SENS[0].t, LIGNES_SENS[2].t,
            'Un carré de côté 7 cm a un périmètre de 28 cm.',
            'Un rectangle de 3 cm sur 5 cm a un périmètre de 15 cm.'],
        [0, 1, 0, 1],
        '<p><b>Vrai ou faux ?</b></p>',
        opts(L1),
    );
};

// ─── Format 5 : l'inverse, échafaudé (texte à trous, saisie) ──────────────

const genInverseTrouSaisie = () => {
    if (Math.random() < 0.5) {
        // carré : P → côté (les 4 côtés égaux, division en mots)
        const coteMm = randInt(3, 12) * 10;
        const P = mmVersCm(4 * coteMm);
        const phrase = `Le périmètre ${P} cm est la somme des 4 côtés égaux : chaque côté vaut {{${mmVersCm(coteMm)}}} cm`;
        return makeTexteTrou(
            phrase,
            `<p><b>Un carré a un périmètre de ${P} cm. Quelle est la longueur de son côté ?</b></p>`,
            { ...opts(L1), mode: 'saisie' },
        );
    }
    // rectangle : P et longueur → demi-périmètre puis largeur
    const Lmm = randInt(5, 12) * 10;
    const lmm = randInt(2, Lmm / 10 - 1) * 10;
    const P = mmVersCm(2 * (Lmm + lmm));
    const demi = mmVersCm(Lmm + lmm);
    const phrase = `La longueur + la largeur font la moitié du périmètre : {{${demi}}} cm ; `
        + `la longueur vaut ${mmVersCm(Lmm)} cm, donc la largeur vaut {{${mmVersCm(lmm)}}} cm`;
    return makeTexteTrou(
        phrase,
        `<p><b>Un rectangle a un périmètre de ${P} cm et une longueur de ${mmVersCm(Lmm)} cm. Quelle est sa largeur ?</b></p>`,
        { ...opts(L1), mode: 'saisie' },
    );
};

// ─── Contrat distracteurs (référence de revue) ────────────────────────────

export const REGLES_DISTRACTEURS_QCM = {
    invariants_communs: [
        'arithmétique en millimètres ENTIERS (les « ,5 » exacts), division en mots, jamais ÷',
        'figures construites DEPUIS les côtés (le L re-dérive chaque cote des coordonnées, garde qui throw) ; toutes les cotes du L données',
        'la formule 2 × (L + l), hors programme du CM, n\'apparaît dans AUCUN choix du QCM-calcul (seconde bonne réponse sinon)',
    ],
    perim_qcm_unique: {
        support: 'deux sous-types : le sens du mot, le calcul à choisir (somme des quatre côtés = la procédure CM)',
        fixes: {
            confusion_aire: 'L × l, ou « la surface qu\'elle occupe » — L\'ERREUR CIBLE',
            demi_tour: 'L + l : la moitié du tour',
            cote_oublie: 'L + L + l : un côté oublié',
            un_seul_cote: 'le plus grand côté',
            compte_de_cotes: 'compter les côtés au lieu de les mesurer',
        },
    },
};

// ─── Registre et point d'entrée ───────────────────────────────────────────

const GENERATORS = {
    perim_figure_saisie: genFigureSaisie,
    perim_calcul_saisie: genCalculSaisie,
    perim_qcm_unique: genQcm,
    perim_tableau_vf: genVraiFaux,
    perim_inverse_texte_trou_saisie: genInverseTrouSaisie,
};

const FORMAT_LABELS = {
    perim_figure_saisie: 'Figure',
    perim_calcul_saisie: 'Calcul',
    perim_qcm_unique: 'QCM',
    perim_tableau_vf: 'Vrai/Faux',
    perim_inverse_texte_trou_saisie: 'Inverse',
};

export const CATEGORIES = [
    {
        id: 'perim',
        label: L1,
        formatsList: Object.keys(GENERATORS).map((k) => ({ key: k, label: FORMAT_LABELS[k] || k })),
    },
];

export const generate = (typeExercice) => {
    let key;
    if (typeExercice && GENERATORS[typeExercice]) key = typeExercice;
    else if (typeExercice) {
        const matching = Object.keys(GENERATORS).filter(k => k.startsWith(typeExercice) || k.includes(typeExercice));
        key = matching.length ? randomChoice(matching) : randomChoice(Object.keys(GENERATORS));
    } else key = randomChoice(Object.keys(GENERATORS));
    const payload = GENERATORS[key]();
    payload._meta.generatorKey = key;
    return payload;
};
